import {useEffect, useState} from "react";

import Loading from "@/components/ui/loading";
import {Button, Modal, Table} from "react-bootstrap";

import api from "@/lib/api";

type ModuleModalProps = {
  moduleId: number;
  datasetId: number;
  showModuleModal: boolean;
  handleCloseModuleModal: () => void;
  handleOpenItemModal: (
    itemId: number,
    items: ItemProps[],
    currentIndex: number,
    moduleIdForItem?: number
  ) => void;
  goBackToDataset: () => void;
};

type ItemProps = {
  value: number;
  label: string;
  description: string;
  item_type_title: string;
};

type ModuleModalData = {
  dataset: {
    value: number;
    label: string;
  };
  module: {
    value: number;
    label: string;
  };
  items: {
    value: number;
    label: string;
    description: string;
    item_type_title: string;
  }[];
};

export async function getModuleModalData(
  dataset_id: number,
  module_id: number
) {
  try {
    const response = await api.get<ModuleModalData>(
      `/hrd/dataset/${dataset_id}/module/${module_id}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching harmonzied data:", error);
    throw new Error("Failed to fetch harmonzied data");
  }
}

export default function ModuleModal({
  showModuleModal,
  moduleId,
  datasetId,
  handleCloseModuleModal,
  handleOpenItemModal,
  goBackToDataset,
}: ModuleModalProps) {
  const [moduleData, setModuleData] = useState<ModuleModalData>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (datasetId && moduleId) {
        setLoading(true);
        const response = await getModuleModalData(datasetId, moduleId);
        setModuleData(response);
        setLoading(false);
      }
    };
    fetchData();
  }, [showModuleModal, moduleId, datasetId]);

  return (
    <Modal
      show={showModuleModal}
      onHide={handleCloseModuleModal}
      size='lg'
      aria-labelledby='module-item-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>{moduleData?.module.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Loading />
        ) : (
          <>
            <p>
              <span className='g2-text-grey'>
                <span
                  className='g2-link-blue'
                  onClick={() => {
                    handleCloseModuleModal();
                  }}
                >
                  Harmonized Data
                </span>{" "}
                &gt;{" "}
                <span
                  className='g2-link-blue'
                  onClick={() => {
                    goBackToDataset();
                    handleCloseModuleModal();
                  }}
                >
                  {moduleData?.dataset.label}
                </span>{" "}
                &gt;{" "}
                <span className='g2-text-grey'>{moduleData?.module.label}</span>{" "}
                &gt;{" "}
                <span className='g2-text-grey'>
                  Items ({moduleData?.items?.length || 0})
                </span>
              </span>
            </p>

            <Table
              className='table table-striped table-hover table-bordered'
              width='100%'
            >
              <thead className='table-dark'>
                <tr className='g2-text-white fw-bold'>
                  <th>LABEL</th>
                  <th>TYPE</th>
                  <th>DESCRIPTION</th>
                </tr>
              </thead>
              <tbody>
                {moduleData?.items.map(
                  (
                    item: {
                      value: number;
                      label: string;
                      description: string;
                      item_type_title: string;
                    },
                    index: number
                  ) => (
                    <tr key={index}>
                      <td>
                        <span
                          className='g2-link-blue'
                          onClick={() => {
                            handleOpenItemModal(
                              item.value,
                              moduleData.items,
                              index,
                              moduleId
                            );
                            handleCloseModuleModal();
                          }}
                        >
                          {item.label}
                        </span>
                      </td>
                      <td>{item.item_type_title} </td>
                      <td>{item.description}</td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className='g2-btn-blue'
          onClick={() => {
            goBackToDataset();
            handleCloseModuleModal();
          }}
        >
          Back to Datasets
        </Button>
        <Button variant='secondary' onClick={handleCloseModuleModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
